import { useCallback, useRef } from 'react';

import type { ConnectionState, DriverStatus, TransportLogEntry } from '@/lib/sync/types';

export interface FollowDebugInput {
  driftMs: number;
  connectionState: ConnectionState;
  driverStatus: DriverStatus;
  clockOffset: number;
  clockRtt: number;
  heartbeatCount: number;
  lastHeartbeatMs: number | null;
  playbackRate: number;
  currentSectionIndex: number;
  sectionCount: number;
  transportLog: TransportLogEntry[];
}

export interface FollowDebugData {
  driftMs: number;
  connectionState: ConnectionState;
  driverStatus: DriverStatus;
  clockOffset: number;
  clockRtt: number;
  heartbeatCount: number;
  lastHeartbeatMs: number | null;
  playbackRate: number;
  currentSectionIndex: number;
  sectionCount: number;
  transportLog: TransportLogEntry[];
  downloadLog: () => void;
}

export function useFollowDebug(input: FollowDebugInput, enabled: boolean): FollowDebugData | null {
  const sessionStartRef = useRef<number>(Date.now());

  const downloadLog = useCallback(() => {
    const exportData = {
      metadata: {
        downloadedAt: new Date().toISOString(),
        sessionStartedAt: new Date(sessionStartRef.current).toISOString(),
        browser: navigator.userAgent,
        sectionCount: input.sectionCount,
      },
      sync: {
        finalDriftMs: input.driftMs,
        clockOffset: input.clockOffset,
        clockRtt: input.clockRtt,
        connectionState: input.connectionState,
        driverStatus: input.driverStatus,
      },
      session: {
        heartbeatCount: input.heartbeatCount,
        transportEventCount: input.transportLog.length,
        durationMs: Date.now() - sessionStartRef.current,
      },
      transportLog: input.transportLog,
    };

    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `follow-debug-${timestamp}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [input]);

  if (!enabled) return null;

  return {
    driftMs: input.driftMs,
    connectionState: input.connectionState,
    driverStatus: input.driverStatus,
    clockOffset: input.clockOffset,
    clockRtt: input.clockRtt,
    heartbeatCount: input.heartbeatCount,
    lastHeartbeatMs: input.lastHeartbeatMs,
    playbackRate: input.playbackRate,
    currentSectionIndex: input.currentSectionIndex,
    sectionCount: input.sectionCount,
    transportLog: input.transportLog,
    downloadLog,
  };
}
